import React, { useCallback, useEffect, useState } from "react";
import { MdAttachFile, MdClose, MdConfirmationNumber, MdSend } from "react-icons/md";
import { toast } from "react-toastify";
import Modal from "../../../../components/modal";
import AnexosInput from "../../../../components/anexos/AnexosInput";
import AnexosLista from "../../../../components/anexos/AnexosLista";
import { mensagemDeErro } from "../../../../components/anexos/anexosRegras";
import { TicketAttachment } from "../../../../interfaces/Ticket";
import { downloadClientTicketAttachment, getClientTicket, sendClientTicketMessage } from "./ticketApi";
import {
  CLIENT_TICKET_STATE_LABELS,
  CLIENT_TICKET_STATE_TONE,
  ClientTicket,
} from "./ticketTypes";
import { mensagemDoServidor } from "../../../../utils/mensagemDoServidor";

interface Props {
  ticketId: number;
  onClose: () => void;
  /** Avisa a lista para recarregar — comentar pode mudar o estado (ex.: sai de "Aguardando você"). */
  onChanged: () => void;
}

const fmtDateTime = (iso?: string) =>
  iso ? new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : undefined;

/**
 * Detalhe do chamado sob o olhar do cliente: estado traduzido, descrição, linha do tempo de datas
 * públicas e comentários públicos. Nada de fila, responsável, nota interna ou SLA pausado (contrato:
 * `ClientTicketViewModel` já vem sem esses campos — este componente não teria como exibi-los mesmo
 * que quisesse). Só é montado para o cliente real: `TicketsTab` nem tenta abrir chamados em preview
 * interno, porque a leitura também exige o vínculo `ClientUserEntity` que um usuário interno não tem.
 */
const TicketDetailModal: React.FC<Props> = ({ ticketId, onClose, onChanged }) => {
  const [ticket, setTicket] = useState<ClientTicket | null>(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState("");
  const [arquivos, setArquivos] = useState<File[]>([]);
  const [sending, setSending] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    getClientTicket(ticketId)
      .then(setTicket)
      .catch((e: any) => {
        toast.error(mensagemDoServidor(e, "Erro ao carregar o chamado."));
        onClose();
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId]);

  useEffect(load, [load]);

  const baixarAnexo = async (a: TicketAttachment) => {
    try {
      await downloadClientTicketAttachment(ticketId, a.id, a.fileName);
    } catch {
      toast.error("Não foi possível baixar o anexo.");
    }
  };

  const podeEnviar = comment.trim().length > 0 || arquivos.length > 0;

  const handleSendComment = async () => {
    const body = comment.trim();
    if (!podeEnviar) return;
    setSending(true);
    try {
      await sendClientTicketMessage(ticketId, { body, arquivos });
      setComment("");
      setArquivos([]);
      load();
      onChanged();
    } catch (e: any) {
      toast.error(mensagemDeErro(e, "Erro ao enviar o comentário."));
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal onClose={onClose}>
      <div
        className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-dv-lg dark:bg-navy-800"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative overflow-hidden bg-dv-gradient px-6 py-5 text-white">
          <div className="absolute -right-8 -top-10 h-32 w-32 rounded-full bg-white/10 blur-2xl" />
          <div className="relative flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/15 backdrop-blur">
                <MdConfirmationNumber className="h-6 w-6" aria-hidden="true" />
              </div>
              <div className="min-w-0">
                <p className="font-display text-[11px] font-bold uppercase tracking-[0.18em] text-white/70">
                  Chamado {ticket?.number ?? ""}
                </p>
                <h3 className="truncate text-lg font-extrabold leading-tight" title={ticket?.subject}>
                  {ticket?.subject ?? "Carregando…"}
                </h3>
              </div>
            </div>
            <button
              aria-label="Fechar"
              onClick={onClose}
              className="rounded-xl p-1.5 text-white/80 transition-colors hover:bg-white/10 hover:text-white"
            >
              <MdClose className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto px-6 py-5">
          {loading || !ticket ? (
            <div className="flex items-center justify-center py-16">
              <div className="h-7 w-7 animate-spin rounded-full border-2 border-dv-blue-500 border-t-transparent" />
            </div>
          ) : (
            <div className="space-y-5">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`rounded-full px-3 py-1 text-xs font-bold ${CLIENT_TICKET_STATE_TONE[ticket.state]}`}>
                  {CLIENT_TICKET_STATE_LABELS[ticket.state]}
                </span>
                <span className="text-xs text-gray-600 dark:text-gray-400">
                  Aberto em {fmtDateTime(ticket.openedAt)}
                </span>
                {ticket.resolvedAt && (
                  <span className="text-xs text-gray-600 dark:text-gray-400">
                    · Resolvido em {fmtDateTime(ticket.resolvedAt)}
                  </span>
                )}
                {ticket.closedAt && (
                  <span className="text-xs text-gray-600 dark:text-gray-400">
                    · Fechado em {fmtDateTime(ticket.closedAt)}
                  </span>
                )}
                {/* Prazo prometido — o backend expõe justamente porque é o que o cliente tem direito de saber do SLA. */}
                {ticket.slaResolutionDueAt && !ticket.resolvedAt && !ticket.closedAt && (
                  <span className="text-xs font-semibold text-dv-blue-700 dark:text-dv-cyan-300">
                    · Previsão de resolução: {fmtDateTime(ticket.slaResolutionDueAt)}
                  </span>
                )}
                {/* A classificação (dele ou corrigida pela triagem). Vê-la é o que permite ao
                    cliente apontar quando entendemos outra coisa. */}
                {ticket.categoryPath && (
                  <span className="text-xs text-gray-600 dark:text-gray-400">
                    · {ticket.categoryPath}
                  </span>
                )}
              </div>

              <div>
                <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Descrição
                </p>
                <p className="whitespace-pre-wrap rounded-xl border border-gray-100 bg-gray-50 p-3 text-sm text-gray-800 dark:border-white/5 dark:bg-navy-900 dark:text-gray-200">
                  {ticket.description}
                </p>
              </div>

              {ticket.attachments && ticket.attachments.length > 0 && (
                <div>
                  <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    <MdAttachFile className="h-4 w-4" aria-hidden="true" />
                    Anexos ({ticket.attachments.length})
                  </p>
                  <AnexosLista anexos={ticket.attachments} onBaixar={(a) => void baixarAnexo(a)} />
                </div>
              )}

              <div>
                <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Comentários
                </p>
                {!ticket.comments || ticket.comments.length === 0 ? (
                  <p className="rounded-xl border border-dashed border-gray-200 py-6 text-center text-sm text-gray-600 dark:border-white/10 dark:text-gray-400">
                    Nenhum comentário ainda.
                  </p>
                ) : (
                  <div className="flex flex-col gap-2.5">
                    {ticket.comments.map((c) => (
                      <div
                        key={c.id}
                        className="rounded-xl border border-gray-100 bg-gray-50 px-3.5 py-2.5 dark:border-white/5 dark:bg-navy-900"
                      >
                        <div className="mb-1 flex items-center justify-between gap-2">
                          {/* `authorIsInternal` é o dado real de quem escreveu — nada de adivinhar
                              pelo nome ausente. Alinha a bolha ao lado certo da conversa. */}
                          <span
                            className={`text-xs font-semibold ${
                              c.authorIsInternal
                                ? "text-dv-blue-700 dark:text-dv-cyan-300"
                                : "text-navy-700 dark:text-white"
                            }`}
                          >
                            {c.authorIsInternal ? c.authorName ?? "Equipe DataValue" : c.authorName ?? "Você"}
                          </span>
                          <span className="shrink-0 text-[11px] text-gray-500 dark:text-gray-400">
                            {fmtDateTime(c.dtCreated)}
                          </span>
                        </div>
                        {c.body && (
                          <p className="whitespace-pre-wrap text-sm text-gray-800 dark:text-gray-200">{c.body}</p>
                        )}
                        {c.attachments && c.attachments.length > 0 && (
                          <div className="mt-2">
                            <AnexosLista anexos={c.attachments} onBaixar={(a) => void baixarAnexo(a)} />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer — caixa de comentário público. Este modal só é montado para o cliente real
            (`TicketsTab` bloqueia a aba inteira em preview interno, D6), então não precisa mais
            gatear por `readOnly` aqui. */}
        {!loading && ticket && (
          <div className="border-t border-gray-100 px-6 py-4 dark:border-gray-700">
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <AnexosInput value={arquivos} onChange={setArquivos} disabled={sending}>
                  <textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    aria-label="Escreva uma resposta"
                    placeholder="Escreva uma resposta…"
                    rows={2}
                    className="w-full resize-none rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800 transition-all focus:border-dv-blue-500 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/20 dark:border-white/10 dark:bg-navy-900 dark:text-white"
                  />
                </AnexosInput>
              </div>
              <button
                onClick={handleSendComment}
                disabled={sending || !podeEnviar}
                className="dv-sheen flex h-[42px] items-center gap-1.5 rounded-xl bg-dv-gradient px-4 text-sm font-bold text-white shadow-dv-glow transition-all active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-50"
              >
                <MdSend className="h-4 w-4" aria-hidden="true" />
                {sending ? "Enviando…" : "Enviar"}
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default TicketDetailModal;
